import { supabase } from '../lib/supabase'
import { VirtualPortfolio, VirtualHolding, VirtualOrder, PortfolioMode } from '../types/investment'
import { marketDataService } from './marketDataService'

export const PORTFOLIO_MODES: PortfolioMode[] = [
  {
    id: 'beginner',
    name: 'Beginner',
    description: 'Perfect for learning the basics of investing',
    startingBalance: 5000,
    features: ['Simplified interface', 'Educational tips', 'Risk warnings'],
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 'intermediate',
    name: 'Intermediate',
    description: 'Standard features for regular trading practice',
    startingBalance: 10000,
    features: ['Full trading features', 'Portfolio analytics', 'Market insights'],
    color: 'from-blue-500 to-cyan-600'
  },
  {
    id: 'advanced',
    name: 'Advanced',
    description: 'Professional-grade tools and analytics',
    startingBalance: 50000,
    features: ['Advanced analytics', 'Risk metrics', 'Backtesting tools'],
    color: 'from-purple-500 to-violet-600'
  }
]

class VirtualTradingService {
  /**
   * Initialize a virtual portfolio for a user
   */
  async initializePortfolio(userId: string, mode: 'beginner' | 'intermediate' | 'advanced'): Promise<VirtualPortfolio> {
    console.log(`🎯 VirtualTradingService: Initializing ${mode} portfolio for user ${userId}`)

    const portfolioMode = PORTFOLIO_MODES.find(m => m.id === mode)
    if (!portfolioMode) {
      throw new Error(`Invalid portfolio mode: ${mode}`)
    }

    try {
      // Check if portfolio already exists
      const { data: existingPortfolio } = await supabase
        .from('virtual_portfolios')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (existingPortfolio) {
        console.log('Portfolio already exists, returning existing portfolio')
        return this.mapDatabaseToPortfolio(existingPortfolio)
      }

      // Create new portfolio
      const portfolioData = {
        user_id: userId,
        portfolio_mode: mode,
        starting_balance: portfolioMode.startingBalance,
        current_cash: portfolioMode.startingBalance,
        total_portfolio_value: portfolioMode.startingBalance,
        total_gain_loss: 0,
        total_gain_loss_percent: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data: portfolio, error } = await supabase
        .from('virtual_portfolios')
        .insert([portfolioData])
        .select()
        .single()

      if (error) {
        console.error('Error creating portfolio:', error)
        throw new Error(`Failed to create portfolio: ${error.message}`)
      }

      console.log('✅ Portfolio created successfully:', portfolio.id)
      return this.mapDatabaseToPortfolio(portfolio)
    } catch (error) {
      console.error('Error initializing portfolio:', error)
      throw error
    }
  }

  /**
   * Get user's virtual portfolio
   */
  async getPortfolio(userId: string): Promise<VirtualPortfolio | null> {
    try {
      const { data: portfolio, error } = await supabase
        .from('virtual_portfolios')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // No portfolio found
        }
        throw new Error(`Failed to fetch portfolio: ${error.message}`)
      }

      return this.mapDatabaseToPortfolio(portfolio)
    } catch (error) {
      console.error('Error fetching portfolio:', error)
      throw error
    }
  }

  /**
   * Get user's holdings
   */
  async getHoldings(userId: string): Promise<VirtualHolding[]> {
    try {
      const { data: holdings, error } = await supabase
        .from('virtual_holdings')
        .select('*')
        .eq('user_id', userId)
        .order('market_value', { ascending: false })

      if (error) {
        console.error('Error fetching holdings:', error)
        return []
      }

      // Update current prices for all holdings
      const updatedHoldings = await Promise.all(
        holdings.map(async (holding) => {
          try {
            const quote = await marketDataService.fetchStockQuote(holding.symbol)
            const marketValue = holding.shares * quote.currentPrice
            const gainLoss = marketValue - (holding.shares * holding.average_cost)
            const gainLossPercent = holding.shares * holding.average_cost !== 0 
              ? (gainLoss / (holding.shares * holding.average_cost)) * 100 
              : 0

            // Update holding with current price
            await supabase
              .from('virtual_holdings')
              .update({
                current_price: quote.currentPrice,
                market_value: marketValue,
                gain_loss: gainLoss,
                gain_loss_percent: gainLossPercent,
                last_updated: new Date().toISOString()
              })
              .eq('id', holding.id)

            return this.mapDatabaseToHolding({
              ...holding,
              current_price: quote.currentPrice,
              market_value: marketValue,
              gain_loss: gainLoss,
              gain_loss_percent: gainLossPercent
            })
          } catch (error) {
            console.error(`Error updating holding ${holding.symbol}:`, error)
            return this.mapDatabaseToHolding(holding)
          }
        })
      )

      return updatedHoldings
    } catch (error) {
      console.error('Error fetching holdings:', error)
      return []
    }
  }

  /**
   * Execute a buy order
   */
  async executeBuyOrder(userId: string, symbol: string, quantity: number): Promise<VirtualOrder> {
    console.log(`💰 Executing buy order: ${quantity} shares of ${symbol} for user ${userId}`)

    try {
      // Get current portfolio and stock quote
      const [portfolio, quote] = await Promise.all([
        this.getPortfolio(userId),
        marketDataService.fetchStockQuote(symbol)
      ])

      if (!portfolio) {
        throw new Error('Portfolio not found. Please initialize your portfolio first.')
      }

      const totalCost = quantity * quote.currentPrice
      const commission = 0 // No commission for virtual trading

      // Check if user has sufficient cash
      if (portfolio.currentCash < totalCost + commission) {
        throw new Error(`Insufficient cash. Required: $${(totalCost + commission).toFixed(2)}, Available: $${portfolio.currentCash.toFixed(2)}`)
      }

      // Check if quantity is valid
      if (quantity <= 0 || quantity !== Math.floor(quantity)) {
        throw new Error('Quantity must be a positive whole number')
      }

      // Create order record
      const orderData = {
        user_id: userId,
        symbol: symbol.toUpperCase(),
        company_name: quote.companyName,
        order_type: 'buy',
        quantity,
        price: quote.currentPrice,
        total_amount: totalCost + commission,
        status: 'completed',
        created_at: new Date().toISOString(),
        executed_at: new Date().toISOString()
      }

      const { data: order, error: orderError } = await supabase
        .from('virtual_orders')
        .insert([orderData])
        .select()
        .single()

      if (orderError) {
        throw new Error(`Failed to create order: ${orderError.message}`)
      }

      // Update portfolio cash balance
      await supabase
        .from('virtual_portfolios')
        .update({
          current_cash: portfolio.currentCash - totalCost - commission,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)

      // Update or create holding
      await this.updateHolding(userId, symbol, quantity, quote.currentPrice, 'buy')

      console.log('✅ Buy order executed successfully:', order.id)
      return this.mapDatabaseToOrder(order)
    } catch (error) {
      console.error('Error executing buy order:', error)
      throw error
    }
  }

  /**
   * Execute a sell order
   */
  async executeSellOrder(userId: string, symbol: string, quantity: number): Promise<VirtualOrder> {
    console.log(`💰 Executing sell order: ${quantity} shares of ${symbol} for user ${userId}`)

    try {
      // Get current holdings and stock quote
      const [holdings, quote] = await Promise.all([
        this.getHoldings(userId),
        marketDataService.fetchStockQuote(symbol)
      ])

      const holding = holdings.find(h => h.symbol === symbol.toUpperCase())
      if (!holding) {
        throw new Error(`You don't own any shares of ${symbol}`)
      }

      if (holding.shares < quantity) {
        throw new Error(`Insufficient shares. You own ${holding.shares} shares, trying to sell ${quantity}`)
      }

      // Check if quantity is valid
      if (quantity <= 0 || quantity !== Math.floor(quantity)) {
        throw new Error('Quantity must be a positive whole number')
      }

      const totalAmount = quantity * quote.currentPrice
      const commission = 0 // No commission for virtual trading

      // Create order record
      const orderData = {
        user_id: userId,
        symbol: symbol.toUpperCase(),
        company_name: quote.companyName,
        order_type: 'sell',
        quantity,
        price: quote.currentPrice,
        total_amount: totalAmount - commission,
        status: 'completed',
        created_at: new Date().toISOString(),
        executed_at: new Date().toISOString()
      }

      const { data: order, error: orderError } = await supabase
        .from('virtual_orders')
        .insert([orderData])
        .select()
        .single()

      if (orderError) {
        throw new Error(`Failed to create order: ${orderError.message}`)
      }

      // Update portfolio cash balance
      const portfolio = await this.getPortfolio(userId)
      if (portfolio) {
        await supabase
          .from('virtual_portfolios')
          .update({
            current_cash: portfolio.currentCash + totalAmount - commission,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
      }

      // Update holding
      await this.updateHolding(userId, symbol, quantity, quote.currentPrice, 'sell')

      console.log('✅ Sell order executed successfully:', order.id)
      return this.mapDatabaseToOrder(order)
    } catch (error) {
      console.error('Error executing sell order:', error)
      throw error
    }
  }

  /**
   * Update holding after buy/sell order
   */
  private async updateHolding(userId: string, symbol: string, quantity: number, price: number, action: 'buy' | 'sell'): Promise<void> {
    try {
      const { data: existingHolding } = await supabase
        .from('virtual_holdings')
        .select('*')
        .eq('user_id', userId)
        .eq('symbol', symbol.toUpperCase())
        .single()

      if (existingHolding) {
        if (action === 'buy') {
          // Update average cost and shares
          const newShares = existingHolding.shares + quantity
          const newAverageCost = ((existingHolding.shares * existingHolding.average_cost) + (quantity * price)) / newShares

          await supabase
            .from('virtual_holdings')
            .update({
              shares: newShares,
              average_cost: newAverageCost,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingHolding.id)
        } else {
          // Sell - reduce shares, keep average cost
          const newShares = existingHolding.shares - quantity

          if (newShares <= 0) {
            // Delete holding if all shares sold
            await supabase
              .from('virtual_holdings')
              .delete()
              .eq('id', existingHolding.id)
          } else {
            await supabase
              .from('virtual_holdings')
              .update({
                shares: newShares,
                updated_at: new Date().toISOString()
              })
              .eq('id', existingHolding.id)
          }
        }
      } else if (action === 'buy') {
        // Create new holding
        const holdingData = {
          user_id: userId,
          symbol: symbol.toUpperCase(),
          company_name: '', // Will be updated by quote
          shares: quantity,
          average_cost: price,
          current_price: price,
          market_value: quantity * price,
          gain_loss: 0,
          gain_loss_percent: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }

        await supabase
          .from('virtual_holdings')
          .insert([holdingData])
      }
    } catch (error) {
      console.error('Error updating holding:', error)
      throw error
    }
  }

  /**
   * Get order history for a user
   */
  async getOrderHistory(userId: string): Promise<VirtualOrder[]> {
    try {
      const { data: orders, error } = await supabase
        .from('virtual_orders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching order history:', error)
        return []
      }

      return orders.map(order => this.mapDatabaseToOrder(order))
    } catch (error) {
      console.error('Error fetching order history:', error)
      return []
    }
  }

  /**
   * Calculate total portfolio value
   */
  async calculatePortfolioValue(userId: string): Promise<number> {
    try {
      const [portfolio, holdings] = await Promise.all([
        this.getPortfolio(userId),
        this.getHoldings(userId)
      ])

      if (!portfolio) return 0

      const holdingsValue = holdings.reduce((sum, holding) => sum + holding.marketValue, 0)
      return portfolio.currentCash + holdingsValue
    } catch (error) {
      console.error('Error calculating portfolio value:', error)
      return 0
    }
  }

  /**
   * Map database record to VirtualPortfolio
   */
  private mapDatabaseToPortfolio(dbRecord: any): VirtualPortfolio {
    return {
      id: dbRecord.id,
      userId: dbRecord.user_id,
      mode: dbRecord.portfolio_mode,
      startingBalance: dbRecord.starting_balance,
      currentCash: dbRecord.current_cash,
      totalValue: dbRecord.total_portfolio_value,
      totalGainLoss: dbRecord.total_gain_loss,
      totalGainLossPercent: dbRecord.total_gain_loss_percent,
      createdAt: dbRecord.created_at,
      updatedAt: dbRecord.updated_at
    }
  }

  /**
   * Map database record to VirtualHolding
   */
  private mapDatabaseToHolding(dbRecord: any): VirtualHolding {
    return {
      id: dbRecord.id,
      userId: dbRecord.user_id,
      symbol: dbRecord.symbol,
      companyName: dbRecord.company_name,
      shares: dbRecord.shares,
      averageCost: dbRecord.average_cost,
      currentPrice: dbRecord.current_price,
      marketValue: dbRecord.market_value,
      gainLoss: dbRecord.gain_loss,
      gainLossPercent: dbRecord.gain_loss_percent,
      lastUpdated: dbRecord.last_updated
    }
  }

  /**
   * Map database record to VirtualOrder
   */
  private mapDatabaseToOrder(dbRecord: any): VirtualOrder {
    return {
      id: dbRecord.id,
      userId: dbRecord.user_id,
      symbol: dbRecord.symbol,
      companyName: dbRecord.company_name,
      orderType: dbRecord.order_type,
      quantity: dbRecord.quantity,
      price: dbRecord.price,
      totalAmount: dbRecord.total_amount,
      status: dbRecord.status,
      createdAt: dbRecord.created_at,
      executedAt: dbRecord.executed_at
    }
  }
}

// Export singleton instance
export const virtualTradingService = new VirtualTradingService()
